import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostOffersSelect } from '@iwoca/lapi-client/edge';

import OfferCard from './OfferCard';
import { getDurationInYears } from '../../helpers/product.helpers';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetOffersResponse } from '../../types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import { Button } from '../Button/Button';
import IconWithText from '../IconWithText/IconWithText';

const ConfirmedOfferCard = ({
  max_amount,
  interest_rate,
  product_type,
  fee_percentage,
  offer_id,
  duration,
}: Pick<
  TGetOffersResponse['data']['offers'][number],
  | 'max_amount'
  | 'interest_rate'
  | 'offer_id'
  | 'product_type'
  | 'fee_percentage'
  | 'duration'
>) => {
  const { formatPercentage } = useNumberFormat();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stateKey = useContextStateKey();
  const { navigateSignup } = useSignupRouterUK();

  const handleClick = async () => {
    setIsLoading(true);
    setError(null);

    if (!stateKey) {
      return null;
    }

    tracking.ampli.instantDecisionCtaClicked(
      getAmplitudeDefaultEventProperties(),
    );

    try {
      await fetchPostOffersSelect({ offerId: offer_id });
      await navigateSignup({ route: 'decision' });
    } catch {
      setError('Something went wrong, please try again.');
    }

    setIsLoading(false);
  };

  const durationLength =
    getDurationInYears(duration) > 1
      ? `${getDurationInYears(duration)} years`
      : `${getDurationInYears(duration)} year`;

  return (
    <OfferCard
      confirmedOffer
      amount={max_amount}
      productDetails={
        <ul>
          <li>
            <IconWithText icon="calendarOutline">
              Up to {durationLength}
            </IconWithText>
          </li>
          <li>
            <IconWithText icon="percent">
              Monthly interest: <span>{formatPercentage(interest_rate)}</span>
            </IconWithText>
          </li>
          {fee_percentage ? (
            <li>
              <IconWithText icon="percent">
                Withdrawal fee:{' '}
                <span>{formatPercentage(fee_percentage / 100)}</span>
              </IconWithText>
            </li>
          ) : null}
        </ul>
      }
      button={
        <Button
          data-testid={`accept-confirmed-${product_type}`}
          onClick={handleClick}
          loading={isLoading}
          variant="secondary"
          size="small"
        >
          Proceed with this offer
        </Button>
      }
      error={error}
    />
  );
};

export default ConfirmedOfferCard;
